import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const fieldClass =
  "h-12 rounded-xl border-white/10 bg-white/5 text-white placeholder:text-white/25 focus-visible:ring-1 focus-visible:ring-white/50";

export const LeadModal = ({ open, onOpenChange, prefill = null, source = "web" }) => {
  const [form, setForm] = useState({ name: "", email: "", brief: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open && prefill) {
      setForm((f) => ({
        ...f,
        name: prefill.name || f.name,
        brief: prefill.brief || f.brief,
      }));
    }
  }, [open, prefill]);

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => {
        setSuccess(false);
        setForm({ name: "", email: "", brief: "" });
        setErrors({});
      }, 300);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Tu nombre es obligatorio.";
    if (!EMAIL_RE.test(form.email.trim())) errs.email = "Ingresa un email válido.";
    if (!form.brief.trim()) errs.brief = "Cuéntanos tu visión.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        brief: form.brief.trim(),
        source,
      });
      setSuccess(true);
      toast.success("Recibido. Angelo te contactará pronto.");
    } catch {
      setSubmitError(true);
      toast.error("Algo falló. Inténtalo de nuevo o escríbenos directo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="lead-modal"
        className="max-w-md rounded-3xl border-white/10 bg-black/85 p-8 backdrop-blur-2xl sm:rounded-3xl"
      >
        {success ? (
          <div
            data-testid="lead-success-state"
            className="flex flex-col items-center gap-4 py-6 text-center"
          >
            <CheckCircle2 className="h-12 w-12 text-white" aria-hidden="true" />
            <h3 className="font-display text-2xl font-semibold tracking-tight text-white">
              Recibido.
            </h3>
            <p className="text-sm leading-relaxed text-white/60">
              Tu imperio está en cola de construcción. Angelo revisará tu visión
              y te contactará en menos de 24 horas.
            </p>
            <a
              href="mailto:angelobusinessai@gmail.com"
              data-testid="lead-mailto-fallback"
              className="text-xs text-white/40 underline-offset-4 transition-colors hover:text-white hover:underline"
            >
              o escríbenos directo: angelobusinessai@gmail.com
            </a>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl font-semibold tracking-tight text-white">
                Build Your Empire<span className="text-white/40">.</span>
              </DialogTitle>
              <DialogDescription className="text-sm text-white/50">
                Cuéntanos tu visión. Angelo te responderá en menos de 24 horas.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="mt-2 flex flex-col gap-4" noValidate>
              <div>
                <label className="mb-1.5 block text-[10px] font-medium tracking-[0.25em] text-white/50">
                  NOMBRE
                </label>
                <Input
                  data-testid="lead-name-input"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Tu nombre"
                  className={fieldClass}
                />
                {errors.name && (
                  <p data-testid="lead-name-error" className="mt-1.5 text-xs text-red-400">
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-[10px] font-medium tracking-[0.25em] text-white/50">
                  EMAIL
                </label>
                <Input
                  data-testid="lead-email-input"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="tu@empresa.com"
                  className={fieldClass}
                />
                {errors.email && (
                  <p data-testid="lead-email-error" className="mt-1.5 text-xs text-red-400">
                    {errors.email}
                  </p>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-[10px] font-medium tracking-[0.25em] text-white/50">
                  PROYECTO
                </label>
                <Textarea
                  data-testid="lead-brief-input"
                  value={form.brief}
                  onChange={set("brief")}
                  placeholder="Describe tu proyecto o el imperio que quieres construir..."
                  rows={4}
                  className={`${fieldClass} h-auto min-h-[100px] resize-none`}
                />
                {errors.brief && (
                  <p data-testid="lead-brief-error" className="mt-1.5 text-xs text-red-400">
                    {errors.brief}
                  </p>
                )}
              </div>
              <button
                type="submit"
                data-testid="lead-submit-button"
                disabled={submitting}
                className="mt-1 h-12 w-full rounded-full bg-white font-display text-sm font-semibold tracking-[0.15em] text-black transition-opacity duration-300 hover:opacity-85 disabled:opacity-50"
              >
                {submitting ? "ENVIANDO..." : "INICIAR CONSTRUCCIÓN"}
              </button>
              {submitError && (
                <p data-testid="lead-submit-error" className="text-center text-xs text-red-400">
                  Algo falló al enviar. Inténtalo de nuevo o usa el correo directo.
                </p>
              )}
              <a
                href="mailto:angelobusinessai@gmail.com"
                data-testid="lead-mailto-fallback"
                className="text-center text-xs text-white/40 underline-offset-4 transition-colors hover:text-white hover:underline"
              >
                o escríbenos directo: angelobusinessai@gmail.com
              </a>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
